/**
 * StepTimeline Component
 *
 * Displays a list of steps with real-time status indicators
 * based on StepReplayProgress events from the SSE stream.
 *
 * Status types:
 * - pending: not yet executed
 * - executing: currently running
 * - completed: succeeded
 * - skipped: failed during recording, skipped during replay (expected)
 * - failed: succeeded during recording but failed during replay (unexpected)
 * - error: runtime error during replay
 */

import React, { useMemo } from 'react';
import { StepReplayProgress } from '../../types/playground';

interface StepTimelineProps {
  steps: Array<{ index: number; action: string; slug: string; value?: string }>;
  progress: StepReplayProgress[];
}

type StepStatus = 'pending' | 'executing' | 'completed' | 'skipped' | 'failed' | 'error';

interface StepState {
  index: number;
  action: string;
  slug: string;
  value?: string;
  status: StepStatus;
  duration_ms?: number;
  reason?: string;
  recording_status?: string;
}

export const StepTimeline: React.FC<StepTimelineProps> = ({
  steps,
  progress,
}) => {
  const stepStates: StepState[] = useMemo(() => {
    // Build a map of latest progress per step_index
    const progressMap = new Map<number, StepReplayProgress>();
    for (const p of progress) {
      progressMap.set(p.step_index, p);
    }

    return steps.map((step) => {
      const prog = progressMap.get(step.index);
      return {
        ...step,
        status: (prog?.status ?? 'pending') as StepStatus,
        duration_ms: prog?.duration_ms,
        reason: prog?.reason,
        recording_status: (prog as any)?.recording_status,
      };
    });
  }, [steps, progress]);

  const completedSteps = stepStates.filter((s) => s.status === 'completed').length;
  const skippedSteps = stepStates.filter((s) => s.status === 'skipped').length;
  const totalDuration = stepStates.reduce(
    (sum, s) => sum + (s.duration_ms ?? 0),
    0
  );

  const statusIcon = (status: StepStatus) => {
    switch (status) {
      case 'completed':
        return (
          <svg className="w-3.5 h-3.5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'skipped':
        return (
          <svg className="w-3.5 h-3.5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
          </svg>
        );
      case 'failed':
      case 'error':
        return (
          <svg className="w-3.5 h-3.5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      case 'executing':
        return (
          <div className="w-3.5 h-3.5 rounded-full bg-yellow-400 animate-pulse" />
        );
      default:
        return (
          <div className="w-3.5 h-3.5 rounded-full bg-gray-300" />
        );
    }
  };

  const statusBg = (status: StepStatus): string => {
    switch (status) {
      case 'completed':
        return 'bg-green-50';
      case 'skipped':
        return 'bg-amber-50';
      case 'failed':
      case 'error':
        return 'bg-red-50';
      case 'executing':
        return 'bg-yellow-50';
      default:
        return '';
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto">
        {steps.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-xs">
            No steps in this recording
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {stepStates.map((step) => (
              <div
                key={step.index}
                className={`px-3 py-2 ${statusBg(step.status)} transition-colors`}
                title={step.reason || undefined}
              >
                <div className="flex items-center gap-2">
                  <div className="flex-shrink-0 w-5 text-center">
                    {statusIcon(step.status)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs truncate ${
                      step.status === 'pending' ? 'text-gray-400' : 'text-gray-800'
                    }`}>
                      <span className="font-medium">{step.action}</span>
                      {step.slug && (
                        <span className="text-gray-400 ml-1">
                          [{step.slug}]
                        </span>
                      )}
                      {step.value && (
                        <span className="text-gray-500 ml-1">
                          &quot;{step.value.length > 20 ? step.value.slice(0, 20) + '...' : step.value}&quot;
                        </span>
                      )}
                    </p>
                    {/* Annotation for skipped/failed steps */}
                    {step.status === 'skipped' && (
                      <p className="text-[10px] text-amber-600 mt-0.5 truncate">
                        Skipped — also failed during recording
                      </p>
                    )}
                    {step.status === 'failed' && step.reason && (
                      <p className="text-[10px] text-red-600 mt-0.5 truncate">
                        {step.reason === 'no_match'
                          ? 'Element not found on page'
                          : step.reason}
                      </p>
                    )}
                  </div>
                  {step.duration_ms != null && (
                    <span className="text-[10px] text-gray-400 flex-shrink-0 tabular-nums">
                      {step.duration_ms}ms
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Summary Bar */}
      {steps.length > 0 && (
        <div className="flex-shrink-0 px-3 py-2 border-t border-gray-200 bg-gray-50">
          <p className="text-[10px] text-gray-500 text-center">
            Total: {(totalDuration / 1000).toFixed(2)}s
            {' · '}
            {completedSteps}/{steps.length} steps
            {skippedSteps > 0 && ` · ${skippedSteps} skipped`}
          </p>
        </div>
      )}
    </div>
  );
};
